import * as THREE from 'three'
import GUI from 'lil-gui'
import gsap from 'gsap'
import { FontLoader } from 'three/examples/jsm/loaders/FontLoader.js'
import { TextGeometry } from 'three/examples/jsm/geometries/TextGeometry.js'
import emailjs from '@emailjs/browser'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'

/**
 * Debug
//  */
// const gui = new GUI()

const parameters = {
    materialColor: '#FAEDE7'
}

// gui
//     .addColor(parameters, 'materialColor')
//     .onChange(() =>
//     {
//         material.color.set(parameters.materialColor)
//         particlesMaterial.color.set(parameters.materialColor)
//     })
// gui.close()

/**
 * Base
 */
// Canvas
const canvas = document.querySelector('canvas.webgl')

// Scene
const scene = new THREE.Scene()

/**
 * Loading Manager
 */
const loaderBar = document.getElementById('loader-bar')
const loaderPercentage = document.getElementById('loader-percentage')
const loaderElement = document.getElementById('loader')

// Query HTML elements for GSAP page load animations
const heroTitle = document.querySelector('.hero-title')
const heroSubtitle = document.querySelector('.hero-subtitle')
const heroButtons = document.querySelector('.hero-buttons')
const navbar = document.querySelector('.navbar')

// Set initial GSAP states for entrance animations
if (heroTitle && heroSubtitle && heroButtons && navbar) {
    gsap.set(heroTitle, { x: 150, opacity: 0 })
    gsap.set(heroSubtitle, { x: 150, opacity: 0 })
    gsap.set(heroButtons, { x: 150, opacity: 0 })
    gsap.set(navbar, { y: -50, opacity: 0 })
}

let allAssetsLoaded = false
let startPlaneAnimation = null
let camera = null
let planeModel = null
let hasTriggeredComplete = false

const loadingProgress = { value: 0 }
let progressTween = null

const triggerLoaderComplete = () => {
    if (hasTriggeredComplete) return
    hasTriggeredComplete = true
    
    // Fade in main page content
    document.body.classList.add('ready')
    
    if (loaderElement) {
        loaderElement.classList.add('loaded')
    }
    
    // Trigger plane fly-in animation
    if (startPlaneAnimation) {
        startPlaneAnimation()
    }
}

const loadingManager = new THREE.LoadingManager(
    // Loaded
    () => {
        allAssetsLoaded = true
        // If progress is not yet at 100%, force it smoothly to 100% over 1.2s
        if (!progressTween || loadingProgress.value < 100) {
            if (progressTween) progressTween.kill()
            progressTween = gsap.to(loadingProgress, {
                value: 100,
                duration: 1.2,
                ease: 'power1.out',
                onUpdate: () => {
                    const currentPercent = Math.round(loadingProgress.value)
                    if (loaderBar) {
                        loaderBar.style.width = `${currentPercent}%`
                    }
                    if (loaderPercentage) {
                        loaderPercentage.textContent = `${currentPercent}%`
                    }
                },
                onComplete: () => {
                    triggerLoaderComplete()
                }
            })
        } else if (Math.round(loadingProgress.value) === 100) {
            triggerLoaderComplete()
        }
    },
    // Progress
    (itemUrl, itemsLoaded, itemsTotal) => {
        const progressRatio = itemsLoaded / itemsTotal
        const targetPercentage = Math.round(progressRatio * 100)
        
        if (progressTween) progressTween.kill()
        
        progressTween = gsap.to(loadingProgress, {
            value: targetPercentage,
            duration: 1.5, // Smoothly animate progress over 1.5s
            ease: 'power1.out',
            onUpdate: () => {
                const currentPercent = Math.round(loadingProgress.value)
                if (loaderBar) {
                    loaderBar.style.width = `${currentPercent}%`
                }
                if (loaderPercentage) {
                    loaderPercentage.textContent = `${currentPercent}%`
                }
            },
            onComplete: () => {
                if (Math.round(loadingProgress.value) === 100 && allAssetsLoaded) {
                    triggerLoaderComplete()
                }
            }
        })
    }
)

/**
 * Models
 */

const gltfLoader = new GLTFLoader(loadingManager)

gltfLoader.load(
    '/models/christmas_ball/scene.gltf',
    (gltf) => {
        const model = gltf.scene
        model.scale.set(0.01, 0.01, 0.01)
        model.position.set(-2, -5, 0)
        scene.add(model)
    },
    undefined,
    (error) => {
        console.error('Error loading GLTF model:', error)
    }
)
let mixer = null

gltfLoader.load(
    '/models/Plane/scene.gltf',
    (gltf) => {
        const model = gltf.scene
        planeModel = model
        // Position visible on the left side of the screen during load
        model.position.set(-4.5, -0.5, 0)
        model.scale.set(1.5, 1.5, 1.5)
        
        // Face the direction of flight (to the right)
        model.rotation.y = Math.PI * 0.5

        mixer = new THREE.AnimationMixer(model)
        const action = mixer.clipAction(gltf.animations[0])

        action.play()
        scene.add(model)

        // Animate the plane flying in and rotating to the front
        startPlaneAnimation = () => {
            const tl = gsap.timeline()
            
            // Camera pans in from left (x = -5, rotation.y = -0.4) to front (x = 0, rotation.y = 0)
            if (camera) {
                tl.to(camera.position, {
                    x: 0,
                    duration: 3.0,
                    ease: 'power2.inOut'
                }, 0)
                
                tl.to(camera.rotation, {
                    y: 0,
                    duration: 3.0,
                    ease: 'power2.inOut'
                }, 0)
            }

            // Plane flies in from left position to right (delay 0.3s relative to camera pan/loader slide)
            tl.to(model.position, {
                x: 2,
                y: -0.5, // Reset to standard y position
                duration: 3.5,
                ease: 'power2.out',
                delay: 0.3
            }, 0)
            
            tl.to(model.rotation, {
                y: 0,
                duration: 2.0,
                ease: 'power2.inOut'
            }, 1.8)

            // Banking tilt (roll) during the turn
            tl.to(model.rotation, {
                z: 1, // Tilt sideways
                duration: 1.0,
                ease: 'power2.out'
            }, 1.8)
            
            tl.to(model.rotation, {
                z: 0, // Level out
                duration: 1.0,
                ease: 'power2.inOut'
            }, 2.8)

            // Nose tilt (pitch) during the turn
            tl.to(model.rotation, {
                x: 0.15, // Tilt nose slightly
                duration: 1.0,
                ease: 'power2.out'
            }, 1.8)
            
            tl.to(model.rotation, {
                x: 0, // Level out
                duration: 1.0,
                ease: 'power2.inOut'
            }, 2.8)

            // HTML content entrance animations (staggered slide-in from right)
            if (navbar) {
                tl.to(navbar, {
                    y: 0,
                    opacity: 1,
                    duration: 1.2,
                    ease: 'power2.out'
                }, 0.5)
            }
            
            if (heroTitle) {
                tl.to(heroTitle, {
                    x: 0,
                    opacity: 1,
                    duration: 1.5,
                    ease: 'power2.out'
                }, 0.5)
            }
            
            if (heroSubtitle) {
                tl.to(heroSubtitle, {
                    x: 0,
                    opacity: 1,
                    duration: 1.5,
                    ease: 'power2.out'
                }, 0.7)
            }
            
            if (heroButtons) {
                tl.to(heroButtons, {
                    x: 0,
                    opacity: 1,
                    duration: 1.5,
                    ease: 'power2.out'
                }, 0.9)
            }
        }

        // If loading manager already completed before this callback finished executing
        if (allAssetsLoaded) {
            startPlaneAnimation()
        }
    }
)

/**
 * Lights
 */
// const directionalLight = new THREE.DirectionalLight('#db3434', 10)
// directionalLight.position.set(2, 2, 0)
// scene.add(directionalLight)

// const pointLight = new THREE.PointLight('#f4d122', 10)
// pointLight.position.set(-2, -3.5, 1)
// pointLight.castShadow = true
// scene.add(pointLight)

// const spotLight = new THREE.SpotLight('#ffffff', 10, 10, Math.PI * 0.1, 0.25, 1)
// spotLight.position.set(2, 2, 0)
// scene.add(spotLight)

/**
 * Textures
 */
const textureLoader = new THREE.TextureLoader(loadingManager)
const particleTexture = textureLoader.load('/textures/particle/4.png')
const gradientTexture = textureLoader.load('/textures/gradients/3.jpg')
gradientTexture.magFilter = THREE.NearestFilter

// Material
const material = new THREE.MeshToonMaterial({ 
    color: parameters.materialColor, 
    gradientMap: gradientTexture 
})

// Mesh
const objectDistance = 4


const mesh3 = new THREE.Mesh(
    new THREE.TorusKnotGeometry(0.7, 0.35, 64, 12),
    material
)

const mesh4 = new THREE.Mesh(
    new THREE.IcosahedronGeometry(0.8, 0),
    material
)

const mesh5 = new THREE.Mesh(
    new THREE.OctahedronGeometry(0.8, 0),
    material
)

const mesh6 = new THREE.Mesh(
    new THREE.DodecahedronGeometry(0.8, 0),
    material
)

const mesh7 = new THREE.Group()
mesh7.position.x = 0
mesh7.position.z = 1

const updateMeshesLayout = () => {
    const isMobile = window.innerWidth < 768;
    const offset = isMobile ? 0 : 2;
    const zOffset = isMobile ? -2 : 0;
    
    mesh3.position.x = offset;
    mesh4.position.x = -offset;
    mesh5.position.x = offset;
    mesh6.position.x = -offset;
    
    mesh3.position.z = zOffset;
    mesh4.position.z = zOffset;
    mesh5.position.z = zOffset;
    mesh6.position.z = zOffset;
    
    // Vertical Y positions (scaled differently for mobile due to stacked content height)
    mesh3.position.y = -objectDistance * (isMobile ? 2.7 : 2);
    mesh4.position.y = -objectDistance * (isMobile ? 3.8 : 3);
    mesh5.position.y = -objectDistance * (isMobile ? 5 : 4);
    mesh6.position.y = -objectDistance * (isMobile ? 6.4 : 5);
    mesh7.position.y = -objectDistance * (isMobile ? 7.4 : 5.5);
    
    // Scale down the Thank You text on mobile
    const textScale = isMobile ? 0.25 : 1;
    mesh7.scale.set(textScale, textScale, textScale);
}

updateMeshesLayout();

scene.add( mesh3, mesh4, mesh5, mesh6, mesh7)

const sectionMeshes = [null, mesh3, mesh4, mesh5, mesh6, mesh7]

const fontLoader = new FontLoader(loadingManager)
fontLoader.load(
    '/fonts/helvetiker_bold.typeface.json',
    (font) => {
        const text = 'THANK YOU!';
        let currentX = 0;
        const letterMeshes = [];
        
        for (let i = 0; i < text.length; i++) {
            const char = text[i];
            
            if (char === ' ') {
                currentX += 0.4;
                continue;
            }
            
            const textGeometry = new TextGeometry(
                char,
                {
                    font: font,
                    size: 0.6,
                    depth: 0.2,
                    curveSegments: 4,
                    bevelEnabled: true,
                    bevelThickness: 0.03,
                    bevelSize: 0.02,
                    bevelOffset: 0,
                    bevelSegments: 2
                }
            )
            textGeometry.computeBoundingBox();
            const width = textGeometry.boundingBox.max.x - textGeometry.boundingBox.min.x;
            
            textGeometry.center();
            const textMesh = new THREE.Mesh(textGeometry, material);
            
            textMesh.position.x = currentX + width / 2;
            mesh7.add(textMesh);
            letterMeshes.push(textMesh);
            
            currentX += width + 0.08;
        }
        
        const totalWidth = currentX - 0.08;
        letterMeshes.forEach(mesh => {
            mesh.position.x -= totalWidth / 2;
            mesh.userData.baseY = mesh.position.y;
        });
    }
)

/**
 * Particles
 */
// Geometry
const particlesCount = 1500
const positions = new Float32Array(particlesCount * 3)
const colors = new Float32Array(particlesCount * 3)
const colorChoices = [new THREE.Color('#aae065'), new THREE.Color('#00ffff'), new THREE.Color('#ffffff')]

for(let i = 0; i < particlesCount; i++){
    positions[i * 3 + 0] = (Math.random() -0.5) * 10
    positions[i * 3 + 1] = objectDistance * 0.5 - Math.random() * objectDistance * 15
    positions[i * 3 + 2] = (Math.random() -0.5) * 10

    const color = colorChoices[Math.floor(Math.random() * colorChoices.length)]
    colors[i * 3 + 0] = color.r
    colors[i * 3 + 1] = color.g
    colors[i * 3 + 2] = color.b
}

const particlesGeometry = new THREE.BufferGeometry()
particlesGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
particlesGeometry.setAttribute('color', new THREE.BufferAttribute(colors, 3))

// Material
const particlesMaterial = new THREE.PointsMaterial({
    sizeAttenuation: true,
    size: 0.2,
    vertexColors: true,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    alphaMap: particleTexture,
})

// Points
const particles = new THREE.Points(particlesGeometry, particlesMaterial)
scene.add(particles)

/**
 * Lights
 */
const light = new THREE.DirectionalLight('#ffffff', 3)
light.position.set(1, 1, 0)
scene.add(light)

/**
 * Sizes
 */
const sizes = {
    width: window.innerWidth,
    height: window.innerHeight
}

let referenceHeight = window.innerHeight;

window.addEventListener('resize', () =>
{
    const isMobile = window.innerWidth < 768;
    // Ignore height-only resizes on mobile (usually the URL bar hiding/showing) to prevent camera jumping
    if (!(isMobile && window.innerWidth === sizes.width)) {
        referenceHeight = window.innerHeight;
    }
    
    // Update sizes
    sizes.width = window.innerWidth
    sizes.height = window.innerHeight

    // Update camera
    camera.aspect = sizes.width / sizes.height
    camera.updateProjectionMatrix()

    // Update renderer
    renderer.setSize(sizes.width, sizes.height)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    
    updateMeshesLayout();
})

/**
 * Camera
 */
// Group
const cameraGroup = new THREE.Group()
scene.add(cameraGroup)

// Base camera
camera = new THREE.PerspectiveCamera(35, sizes.width / sizes.height, 0.1, 100)
camera.position.x = -5
camera.position.z = 6
camera.rotation.y = -0.4
cameraGroup.add(camera)

/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({
    canvas: canvas,
    alpha: true,
    powerPreference: 'high-performance',
    antialias: false
})
renderer.setSize(sizes.width, sizes.height)
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))

/**
 * Scroll
 */
let scrollY = window.scrollY
let currentSection = 0
let footerAnimated = false

window.addEventListener('scroll', () =>
{
    scrollY = window.scrollY
    const newSection = Math.round(scrollY / sizes.height)
    if(newSection != currentSection)
    {
        currentSection = newSection
        
        const activeMesh = sectionMeshes[currentSection - 1]
        if (activeMesh && activeMesh !== mesh7) {
            gsap.to(
                activeMesh.rotation,
                {
                    duration: 1.5,
                    ease: 'power2.inOut',
                    x: '+=6',
                    y: '+=3',
                    z: '+=1.5'
                }
            )
        }
    }
    
    const maxScroll = document.body.scrollHeight - window.innerHeight;
    if (scrollY >= maxScroll - 50) {
        if (!footerAnimated && mesh7.children.length > 0) {
            footerAnimated = true;
            
            mesh7.children.forEach((letterMesh, index) => {
                gsap.to(letterMesh.position, {
                    duration: 0.6,
                    y: letterMesh.userData.baseY + 0.6,
                    ease: 'power2.out',
                    delay: index * 0.08,
                    yoyo: true,
                    repeat: 1
                });
                
                if (letterMesh.userData.targetRotY === undefined) {
                    letterMesh.userData.targetRotY = 0;
                }
                letterMesh.userData.targetRotY += Math.PI * 2;
                
                gsap.to(letterMesh.rotation, {
                    duration: 1.2,
                    y: letterMesh.userData.targetRotY,
                    ease: 'power2.inOut',
                    delay: index * 0.08,
                    overwrite: "auto"
                });
            });
        }
    } else if (scrollY < maxScroll - 200) {
        footerAnimated = false;
    }
})

/**
 * Cursor
 */
const cursor = {}
cursor.x = 0
cursor.y = 0

window.addEventListener('mousemove', (event) =>{
    cursor.x = event.clientX / sizes.width - 0.5
    cursor.y = event.clientY / sizes.height - 0.5
})

/**
 * Animate
 */
const clock = new THREE.Clock()
let previousTime = 0

const tick = () =>
{
    const elapsedTime = clock.getElapsedTime()
    const deltaTime = elapsedTime - previousTime
    previousTime = elapsedTime


    // Animate meshes
    for(const mesh of sectionMeshes)
    {
        if (mesh && mesh !== mesh7) {
            mesh.rotation.x += deltaTime * 0.1
            mesh.rotation.y += deltaTime * 0.12
        }
    }

    // Update mixer for GLTF animations
    if (mixer) {
        mixer.update(deltaTime);
    }

    // Float plane model during load phase
    if (planeModel && !allAssetsLoaded) {
        planeModel.position.y = -0.5 + Math.sin(elapsedTime * 2.5) * 0.08
        planeModel.rotation.z = Math.sin(elapsedTime * 1.5) * 0.04
    }
    
    // Animate particles
    particles.rotation.y += deltaTime * 0.05
    particles.position.y = Math.sin(elapsedTime * 0.2) * 0.5

    // Animate camera
    const targetCameraY = - scrollY / referenceHeight * objectDistance;
    camera.position.y += (targetCameraY - camera.position.y) * 10 * deltaTime;

    const parallaxX = cursor.x * 0.5
    const parallaxY = -cursor.y * 0.5
    
    cameraGroup.position.x += (parallaxX - cameraGroup.position.x) * 5 * deltaTime
    cameraGroup.position.y += (parallaxY - cameraGroup.position.y) * 5 * deltaTime

    // Render
    renderer.render(scene, camera)

    window.requestAnimationFrame(tick)
}

tick()

/**
 * Text and Icon Animation
 */
const animatedGroup = document.querySelector('.animated-group')
const animatedWord = document.querySelector('.animated-word')
const codeIcon = document.querySelector('.code-icon')

if(animatedGroup && animatedWord && codeIcon) {
    const words = ["Ideas", "Concepts", "Designs", "Code"]
    const svgs = [
        // Ideas (Lightbulb)
        `<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><circle cx="12" cy="12" r="12" fill="#cce5ff"/><path d="M12 6a4 4 0 0 0-4 4c0 1.66.92 3.12 2.29 3.86.32.17.51.52.51.89V15h2.4v-.25c0-.37.19-.72.51-.89A4.32 4.32 0 0 0 16 10a4 4 0 0 0-4-4zm-1.2 11h2.4v1h-2.4v-1z" fill="#1e1a20"/></svg>`,
        // Concepts (Overlapping circles)
        `<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><circle cx="12" cy="12" r="12" fill="#cce5ff"/><circle cx="9.5" cy="12" r="3.5" fill="none" stroke="#1e1a20" stroke-width="2"/><circle cx="14.5" cy="12" r="3.5" fill="none" stroke="#1e1a20" stroke-width="2"/></svg>`,
        // Designs (Palette)
        `<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><circle cx="12" cy="12" r="12" fill="#cce5ff"/><path d="M12 6a6 6 0 0 0-6 6c0 3.31 2.69 6 6 6 1.1 0 2-.9 2-2 0-.48-.18-.94-.5-1.28a.95.95 0 0 1-.2-.72c0-.55.45-1 1-1h1.2c2.21 0 4-1.79 4-4a6 6 0 0 0-6-6zm-3.5 6a1.5 1.5 0 1 1 1.5-1.5A1.5 1.5 0 0 1 8.5 12zm2-3a1.5 1.5 0 1 1 1.5-1.5A1.5 1.5 0 0 1 10.5 9zm3 0a1.5 1.5 0 1 1 1.5-1.5A1.5 1.5 0 0 1 13.5 9zm2 3a1.5 1.5 0 1 1 1.5-1.5A1.5 1.5 0 0 1 15.5 12z" fill="#1e1a20"/></svg>`,
        // Code
        `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM8.5 15.5L4.5 12L8.5 8.5L9.91 9.91L7.33 12L9.91 14.09L8.5 15.5ZM11.41 16.59L10.05 16.24L12.59 7.41L13.95 7.76L11.41 16.59ZM15.5 15.5L14.09 14.09L16.67 12L14.09 9.91L15.5 8.5L19.5 12L15.5 15.5Z" fill="#cce5ff"/></svg>`
    ]
    let currentIndex = 3 // Start with Code
    setInterval(() => {
        gsap.to(animatedGroup, {
            opacity: 0,
            y: 10,
            duration: 0.5,
            onComplete: () => {
                currentIndex = (currentIndex + 1) % words.length
                animatedWord.textContent = words[currentIndex]
                codeIcon.innerHTML = svgs[currentIndex]
                gsap.fromTo(animatedGroup, 
                    { opacity: 0, y: -10 },
                    { opacity: 1, y: 0, duration: 0.5 }
                )
            }
        })
    }, 2500)
}

/**
 * Contact Form Handling
 */
const contactForm = document.getElementById('contact-form')
const submitBtn = document.getElementById('submit-btn')

if(contactForm) {
    contactForm.addEventListener('submit', (e) => {
        e.preventDefault()
        
        const originalText = submitBtn.textContent
        submitBtn.textContent = 'SENDING...'
        submitBtn.style.opacity = '0.7'
        
        emailjs.sendForm(
            'service_55t28uq',
            'template_3u8odmn',
            contactForm,
            'Jrkni7cSpq9YpfJrK'
        ).then(() => {
            submitBtn.textContent = 'MESSAGE SENT!'
            submitBtn.classList.add('success')
            submitBtn.style.opacity = '1'
            contactForm.reset()
            
            setTimeout(() => {
                submitBtn.textContent = originalText
                submitBtn.classList.remove('success')
            }, 3000)
        }).catch((error) => {
            console.error('EmailJS Error:', error)
            submitBtn.textContent = 'ERROR! TRY AGAIN'
            submitBtn.style.opacity = '1'
            
            setTimeout(() => {
                submitBtn.textContent = originalText
            }, 3000)
        })
    })
}

/**
 * Mobile Navbar Toggle
 */
const hamburger = document.getElementById('hamburger')
const navLinks = document.getElementById('nav-links')
const navBtn = document.getElementById('nav-btn')

if (hamburger && navLinks && navBtn && navbar) {
    hamburger.addEventListener('click', () => {
        hamburger.classList.toggle('active')
        navLinks.classList.toggle('active')
        navBtn.classList.toggle('active')
        navbar.classList.toggle('active')
    })
    
    // Close menu when clicking a link
    navLinks.querySelectorAll('a').forEach(link => {
        link.addEventListener('click', () => {
            hamburger.classList.remove('active')
            navLinks.classList.remove('active')
            navBtn.classList.remove('active')
            navbar.classList.remove('active')
        })
    })
}

/**
 * Mobile Project Carousel
 */
const prevBtn = document.getElementById('carousel-prev')
const nextBtn = document.getElementById('carousel-next')
const projectCards = [
    document.getElementById('card1'),
    document.getElementById('card2'),
    document.getElementById('card3')
]

let currentMobileCard = 0;

function updateMobileCarousel() {
    if (window.innerWidth >= 768) {
        // Desktop: clear mobile classes
        projectCards.forEach(card => {
            if(card) {
                card.classList.remove('mobile-front', 'mobile-back-1', 'mobile-back-2');
            }
        });
        return;
    }
    
    // Mobile logic
    if (!projectCards[0]) return;
    
    const indexFront = currentMobileCard;
    const indexBack1 = (currentMobileCard + 1) % 3;
    const indexBack2 = (currentMobileCard + 2) % 3;
    
    projectCards.forEach(card => card.classList.remove('mobile-front', 'mobile-back-1', 'mobile-back-2'));
    
    projectCards[indexFront].classList.add('mobile-front');
    projectCards[indexBack1].classList.add('mobile-back-1');
    projectCards[indexBack2].classList.add('mobile-back-2');
}

if (prevBtn && nextBtn) {
    prevBtn.addEventListener('click', () => {
        currentMobileCard = (currentMobileCard - 1 + 3) % 3;
        updateMobileCarousel();
    });
    
    nextBtn.addEventListener('click', () => {
        currentMobileCard = (currentMobileCard + 1) % 3;
        updateMobileCarousel();
    });
}

// Call initially and on resize
window.addEventListener('resize', updateMobileCarousel);
updateMobileCarousel();
